/**
 * errorReporter — lightweight error reporting for production.
 *
 * Sends uncaught exceptions to Sentry's Store API when VITE_SENTRY_DSN
 * is set.  Falls back to stderr in development or when no DSN is
 * configured.  No Sentry SDK package required — uses the public HTTP endpoint
 * so the bundle stays lean; add @sentry/react when fuller tracing is needed.
 *
 * Usage:
 *   // In index.tsx — call once at startup
 *   initErrorReporter();
 *
 *   // Anywhere errors must be forwarded (e.g. ErrorBoundary)
 *   captureException(error, { componentStack: info.componentStack });
 */

// ── DSN parsing ───────────────────────────────────────────────────────────────

interface ParsedDSN {
  endpoint: string;
  projectId: string;
  publicKey: string;
}

function parseDSN(dsn: string): ParsedDSN | null {
  try {
    const url = new URL(dsn);
    const publicKey  = url.username;
    const host       = url.host;
    const projectId  = url.pathname.replace(/^\//, '');
    const protocol   = url.protocol;
    const endpoint   = `${protocol}//${host}/api/${projectId}/store/`;
    if (!publicKey || !projectId) return null;
    return { endpoint, projectId, publicKey };
  } catch {
    return null;
  }
}

// ── Internal state ────────────────────────────────────────────────────────────

let _dsn: ParsedDSN | null = null;
let _release              = '';
let _environment          = process.env['NODE_ENV'] ?? 'production';

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Call once at app startup (index.tsx).
 * Reads VITE_SENTRY_DSN from the environment and sets up a global
 * window.onerror handler to catch errors that escape React.
 */
export function initErrorReporter(opts: { release?: string | undefined; environment?: 'development' | 'production' | 'test' | undefined } = {}): void {
  const raw = process.env['VITE_SENTRY_DSN'] ?? '';
  _dsn         = raw ? parseDSN(raw) : null;
  _release     = opts.release ?? process.env['VITE_VERSION'] ?? 'unknown';
  _environment = opts.environment ?? _environment;

  if (!_dsn) return; // No DSN — silent no-op in prod, dev already has devtools

  window.onerror = (message, source, lineno, colno, error) => {
    captureException(error ?? new Error(String(message)), {
      source:  String(source ?? ''),
      lineno:  lineno ?? 0,
      colno:   colno  ?? 0,
    });
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const err = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    captureException(err, { type: 'unhandledrejection' });
  };
}

/**
 * Forward an error to Sentry (fire-and-forget).
 * Safe to call even when no DSN is configured — it logs to stderr in development.
 */
export function captureException(error: Error, extra?: Record<string, unknown>): void {
  if (!_dsn) {
    if (process.env['NODE_ENV'] !== 'test') {
      console.error('[errorReporter]', error, extra);
    }
    return;
  }

  const event = {
    event_id:   crypto.randomUUID().replace(/-/g, ''),
    timestamp:  new Date().toISOString(),
    platform:   'javascript',
    release:    _release,
    environment: _environment,
    exception: {
      values: [{
        type:  error.name,
        value: error.message,
        stacktrace: {
          frames: (error.stack ?? '')
            .split('\n')
            .filter(Boolean)
            .map(line => ({ filename: line })),
        },
      }],
    },
    extra: extra ?? {},
  };

  const p = fetch(_dsn.endpoint, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'X-Sentry-Auth': [
        'Sentry sentry_version=7',
        `sentry_key=${_dsn.publicKey}`,
        `sentry_client=dystest-reporter/1.0`,
      ].join(', '),
    },
    body: JSON.stringify(event),
    // Don't block the UI on error reporting
    keepalive: true,
  });
  // Guard for environments where fetch returns synchronously (e.g. jest mocks
  // that don't return a real Promise object).
  if (p && typeof p.catch === 'function') {
    p.catch(() => {/* swallow — error reporting must never throw */});
  }
}
